import { EyeOff, GripVertical, Maximize2 } from 'lucide-react';
import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { useT } from '../app/i18n.tsx';
import { TileContext } from '../ui/tile.ts';
import { COLS, GAP, height, moveTile, resizeTile, ROW, setTileHidden, type Grid, type Tile } from './dashboard-grid.ts';
import type { WidgetId } from './dashboard-layout.ts';

// The dashboard's board on a wide screen (dashboard-grid.ts): cards on a grid of cells, and in
// editing — with the cells drawn — moved by dragging them and sized by dragging their right edge,
// bottom edge or corner, a whole cell at a time, as widgets on an Android home screen. The
// keyboard does the same on a card's grip (arrows move) and its size button (arrows resize).

type Kind = 'move' | 'e' | 's' | 'se';
type Gesture = { id: WidgetId; kind: Kind; pointerX: number; pointerY: number; origin: Tile; preview: Grid; dx: number; dy: number };

/** Empty rows under the cards while editing: room to put a card lower. */
const SPARE_ROWS = 3;

export function DashboardBoard({ grid, editing, body, onChange }: { grid: Grid; editing: boolean; body: Record<WidgetId, ReactNode>; onChange(next: Grid): void }) {
  const t = useT();
  const [width, setWidth] = useState(0);
  const [gesture, setGesture] = useState<Gesture | null>(null);
  const shown = gesture?.preview ?? grid;
  const visible = shown.tiles.filter((tile) => !shown.hidden.includes(tile.id)).sort((a, b) => a.y - b.y || a.x - b.x);
  const rows = Math.max(1, height(shown) + (editing ? SPARE_ROWS : 0));
  const step = (width + GAP) / COLS;
  const name = (id: WidgetId) => t.dynamic(`dashboard.widgets.${id}`);

  // The board's width, for turning a drag in pixels into cells.
  const observer = useRef<ResizeObserver | null>(null);
  const board = useCallback((element: HTMLDivElement | null) => {
    observer.current?.disconnect();
    if (!element) return;
    setWidth(element.clientWidth);
    observer.current = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.current.observe(element);
  }, []);

  const begin = (id: WidgetId, kind: Kind) => (event: PointerEvent) => {
    if (!editing || event.button !== 0) return;
    // A button on the card's bar does its own thing; the grip and the card itself drag.
    const target = event.target as HTMLElement;
    if (kind === 'move' && target.closest('button:not(.dash-tile__grip), .dash-tile__handle')) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const origin = grid.tiles.find((tile) => tile.id === id);
    if (origin) setGesture({ id, kind, pointerX: event.clientX, pointerY: event.clientY, origin, preview: grid, dx: 0, dy: 0 });
  };
  const follow = (event: PointerEvent) => {
    if (!gesture || step <= 0) return;
    const { id, kind, origin } = gesture;
    const dx = event.clientX - gesture.pointerX;
    const dy = event.clientY - gesture.pointerY;
    const preview =
      kind === 'move'
        ? moveTile(grid, id, origin.x + dx / step, origin.y + dy / ROW)
        : resizeTile(grid, id, kind === 's' ? origin.w : origin.w + dx / step, kind === 'e' ? origin.h : origin.h + dy / ROW);
    setGesture({ ...gesture, preview, dx, dy });
  };
  const finish = () => {
    if (gesture) onChange(gesture.preview);
    setGesture(null);
  };

  // Arrows on the grip move the card a cell; arrows on the size button make it a cell smaller or larger.
  const keys = (tile: Tile, kind: 'move' | 'size') => (event: KeyboardEvent) => {
    const delta: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const d = delta[event.key];
    if (!d) return;
    event.preventDefault();
    onChange(kind === 'move' ? moveTile(grid, tile.id, tile.x + d[0], tile.y + d[1]) : resizeTile(grid, tile.id, tile.w + d[0], tile.h + d[1]));
  };

  const area = (tile: Pick<Tile, 'x' | 'y' | 'w' | 'h'>) => ({ gridColumn: `${tile.x + 1} / span ${tile.w}`, gridRow: `${tile.y + 1} / span ${tile.h}` });
  const active = gesture ? visible.find((tile) => tile.id === gesture.id) : undefined;
  return (
    <div
      ref={board}
      className={`dash-board${editing ? ' dash-board--edit' : ''}`}
      style={{ gridTemplateRows: `repeat(${rows}, ${ROW}px)` }}
      onPointerMove={follow}
      onPointerUp={finish}
      // Released outside the window (no pointerup comes): what was shown is kept, not left hanging.
      onLostPointerCapture={finish}
      onPointerCancel={() => setGesture(null)}
    >
      {/* The grid itself, while editing: every cell a card can take. */}
      {editing && Array.from({ length: rows * COLS }, (_, i) => <span key={`c${i}`} className="dash-cell" style={area({ x: i % COLS, y: Math.floor(i / COLS), w: 1, h: 1 })} />)}
      {gesture?.kind === 'move' && active && <div className="dash-tile-ghost" style={area(active)} />}
      {visible.map((tile) => {
        const moving = gesture?.kind === 'move' && gesture.id === tile.id;
        const shift = moving ? `translate(${gesture.origin.x * step + gesture.dx - tile.x * step}px, ${gesture.origin.y * ROW + gesture.dy - tile.y * ROW}px)` : undefined;
        return (
          <div
            key={tile.id}
            data-tile={tile.id}
            className={['dash-tile', editing ? 'dash-tile--edit' : '', gesture?.id === tile.id ? 'dash-tile--active' : ''].filter(Boolean).join(' ')}
            style={{ ...area(tile), transform: shift }}
            onPointerDown={begin(tile.id, 'move')}
          >
            <div className="dash-tile__body">
              <TileContext.Provider value>{body[tile.id]}</TileContext.Provider>
              {editing && (
                <>
                  <div className="dash-slot__bar">
                    <button type="button" className="icon-btn dash-tile__grip" aria-label={`${t.t('dashboard.layout.move')}: ${name(tile.id)}`} title={t.t('dashboard.layout.move')} onKeyDown={keys(tile, 'move')}>
                      <GripVertical />
                    </button>
                    <strong className="grow truncate">{name(tile.id)}</strong>
                    <span className="dash-tile__size" aria-hidden="true">
                      {tile.w} × {tile.h}
                    </span>
                    <button className="icon-btn" type="button" aria-label={`${t.t('dashboard.layout.hide')}: ${name(tile.id)}`} title={t.t('dashboard.layout.hide')} onClick={() => onChange(setTileHidden(grid, tile.id, true))}>
                      <EyeOff />
                    </button>
                  </div>
                  <span className="dash-tile__handle dash-tile__handle--e" aria-hidden="true" onPointerDown={begin(tile.id, 'e')} />
                  <span className="dash-tile__handle dash-tile__handle--s" aria-hidden="true" onPointerDown={begin(tile.id, 's')} />
                  <button
                    type="button"
                    className="dash-tile__handle dash-tile__handle--se"
                    aria-label={`${t.t('dashboard.layout.resize')}: ${name(tile.id)}, ${tile.w} × ${tile.h}`}
                    title={t.t('dashboard.layout.resize')}
                    onPointerDown={begin(tile.id, 'se')}
                    onKeyDown={keys(tile, 'size')}
                  >
                    <Maximize2 aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
